import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ContactHeader = ({ 
  contact, 
  onEdit, 
  onDelete, 
  onCall, 
  onMessage, 
  onEmail,
  onClose 
}) => {
  const [imageError, setImageError] = useState(false);

  const getBirthdayCountdown = () => {
    if (!contact?.birthday) return null;
    
    const today = new Date();
    const birthday = new Date(contact.birthday);
    const thisYear = today?.getFullYear();
    
    // Set birthday to this year
    birthday?.setFullYear(thisYear);
    
    // If birthday has passed this year, set to next year
    if (birthday < today) {
      birthday?.setFullYear(thisYear + 1);
    }
    
    const diffTime = birthday - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const daysUntilBirthday = getBirthdayCountdown();

  return (
    <div className="bg-surface border-b border-border">
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b border-border">
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
        >
          <Icon name="X" size={24} />
        </Button>
        <h1 className="text-lg font-semibold text-text-primary">Contact Details</h1>
        <Button
          variant="ghost"
          size="icon"
          onClick={onEdit}
        >
          <Icon name="Edit" size={20} />
        </Button>
      </div>
      {/* Contact Profile Section */}
      <div className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-6">
          {/* Profile Photo */}
          <div className="flex justify-center lg:justify-start mb-4 lg:mb-0">
            <div className="relative">
              <div className="w-24 h-24 lg:w-32 lg:h-32 rounded-full overflow-hidden bg-muted border-4 border-surface shadow-card">
                {contact?.avatar && !imageError ? (
                  <Image
                    src={contact?.avatar}
                    alt={contact?.name}
                    className="w-full h-full object-cover"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-primary text-primary-foreground">
                    <Icon name="User" size={32} />
                  </div>
                )}
              </div>
              
              {/* Online Status Indicator */}
              <div className="absolute bottom-1 right-1 w-6 h-6 bg-success rounded-full border-2 border-surface flex items-center justify-center">
                <div className="w-3 h-3 bg-success rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="flex-1 text-center lg:text-left">
            <div className="mb-4">
              <h2 className="text-2xl lg:text-3xl font-bold text-text-primary mb-2">
                {contact?.name}
              </h2>
              
              {/* Tags */}
              {contact?.tags && contact?.tags?.length > 0 && (
                <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-3">
                  {contact?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/20"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Key Details */}
              <div className="space-y-2 text-text-secondary">
                {contact?.company && (
                  <div className="flex items-center justify-center lg:justify-start space-x-2">
                    <Icon name="Building" size={16} />
                    <span>{contact?.company}</span>
                  </div>
                )}
                
                {contact?.birthday && (
                  <div className="flex items-center justify-center lg:justify-start space-x-2">
                    <Icon name="Calendar" size={16} />
                    <span>{new Date(contact.birthday)?.toLocaleDateString()}</span>
                    {daysUntilBirthday !== null && (
                      <span className="text-primary font-medium">
                        ({daysUntilBirthday === 0 ? 'Today!' : `${daysUntilBirthday} days`})
                      </span>
                    )}
                  </div>
                )}
                
                {contact?.location && (
                  <div className="flex items-center justify-center lg:justify-start space-x-2">
                    <Icon name="MapPin" size={16} />
                    <span>{contact?.location}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-3">
              <Button
                variant="default"
                size="sm"
                onClick={onCall}
                iconName="Phone"
                iconPosition="left"
                disabled={!contact?.phone}
              >
                Call
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={onMessage}
                iconName="MessageSquare"
                iconPosition="left"
              >
                Message
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={onEmail}
                iconName="Mail"
                iconPosition="left"
                disabled={!contact?.email}
              >
                Email
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={onEdit}
                iconName="Edit"
                iconPosition="left"
              >
                Edit
              </Button>
            </div>
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex flex-col space-y-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <Icon name="X" size={20} />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              className="text-error hover:text-error"
            >
              <Icon name="Trash2" size={20} />
            </Button>
          </div>
        </div>

        {/* Birthday Countdown Widget */}
        {daysUntilBirthday !== null && daysUntilBirthday <= 30 && (
          <div className="mt-6 p-4 bg-accent/5 border border-accent/20 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                  <Icon name="Gift" size={20} className="text-accent-foreground" />
                </div>
                <div>
                  <p className="font-medium text-text-primary">
                    {daysUntilBirthday === 0 ? 'Birthday Today!' : `Birthday in ${daysUntilBirthday} days`}
                  </p>
                  <p className="text-sm text-text-secondary">
                    {new Date(contact.birthday)?.toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
              
              <Button
                variant="default"
                size="sm"
                onClick={() => onMessage('birthday')}
                iconName="Send"
                iconPosition="left"
              >
                Send Wishes
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactHeader;